import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';

interface PortalDateRangeFilterProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
}

/** From/To date-range filter row shared across the Client Portal's GRN, Dispatch, and Invoices
 * pages — sits above the DataTable's own search/column-filter toolbar since it filters on a
 * different axis (a date range, not text/dropdown matching). Values are plain 'YYYY-MM-DD'
 * strings (native <input type="date"> format) — callers filter their already-loaded rows by
 * comparing each row's date field against these bounds client-side. */
export default function PortalDateRangeFilter({ fromDate, toDate, onFromDateChange, onToDateChange }: PortalDateRangeFilterProps) {
  const { t } = useTranslation('clientPortal');
  const hasFilter = Boolean(fromDate || toDate);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: '10px' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
          <DateRangeOutlinedIcon fontSize="small" />
          <Stack sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{t('dateRangeFilter.label')}</Stack>
        </Stack>
        <TextField
          type="date"
          size="small"
          label={t('dateRangeFilter.from')}
          value={fromDate}
          onChange={(e) => onFromDateChange(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          type="date"
          size="small"
          label={t('dateRangeFilter.to')}
          value={toDate}
          onChange={(e) => onToDateChange(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        />
        {hasFilter && (
          <Button
            size="small"
            startIcon={<ClearIcon fontSize="small" />}
            onClick={() => {
              onFromDateChange('');
              onToDateChange('');
            }}
          >
            {t('dateRangeFilter.clear')}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
